import { useCan } from '@features/user';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Badge, Button } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { useBookingRequestsQuery, useChangeRequestsQuery } from '../store/api';

interface BookingRequestsButtonProps {
  /** Toggle the requests panel (owned by the calendar layout). */
  onToggle: () => void;
}

/** Calendar-header entry point to the pending booking-requests queue.
 *
 * Renders nothing unless the self-scheduling feature gate is on AND the user
 * can manage the org calendar (the queue API is manager-gated). The badge
 * counts everything waiting on staff: pending bookings (including
 * auto-confirmed ones awaiting identity, actionable via Confirm) plus change
 * requests — the same arithmetic as the nav Schedule badge, so the two
 * always agree. */
export function BookingRequestsButton({ onToggle }: BookingRequestsButtonProps) {
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING,
  );
  const canManageScheduling = useCan('manage_org_calendar');
  const { bookingRequests } = useEventTracking();

  const enabled = schedulingFeatureEnabled && canManageScheduling;
  const { data } = useBookingRequestsQuery(
    {},
    { skip: !enabled, pollingInterval: 60_000 },
  );
  const { data: changeRequestData } = useChangeRequestsQuery(undefined, {
    skip: !enabled,
    pollingInterval: 60_000,
  });

  if (!enabled) {
    return null;
  }

  const pendingCount =
    (data?.items.length ?? 0) + (changeRequestData?.items.length ?? 0);

  const handleClick = () => {
    bookingRequests.opened({ pending_count: pendingCount });
    onToggle();
  };

  return (
    <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
      <Button
        variant="outlined"
        size="small"
        startIcon={<InboxOutlinedIcon />}
        onClick={handleClick}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Requests
      </Button>
    </Badge>
  );
}
